import React, { useState } from "react";
import Context from "./index";
import { create } from "../../Api/WebApi";
import { message } from "antd";

const PURCHASE_EVENT_STORAGE_KEY = "naatu_purchase_event_id";

const createPurchaseEventId = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const setPurchaseEventId = (eventId) => {
  if (!eventId) {
    return;
  }

  sessionStorage.setItem(PURCHASE_EVENT_STORAGE_KEY, eventId);
  localStorage.setItem(PURCHASE_EVENT_STORAGE_KEY, eventId);
};

const getPurchaseEventId = (orderId) => {
  const storedEventId =
    sessionStorage.getItem(PURCHASE_EVENT_STORAGE_KEY) ||
    localStorage.getItem(PURCHASE_EVENT_STORAGE_KEY);
  if (storedEventId) {
    return storedEventId;
  }

  if (orderId) {
    return `order_${orderId}`;
  }

  return null;
};

// ─── Enhanced Purchase Tracking: GA4 + Meta Pixel ────────
// Note: Backend (phone_callback.php) handles detailed product-level tracking
// Frontend does fast real-time tracking with order totals
const fireBrowserPurchase = async (
  orderId,
  orderValue,
  purchaseEventId = null,
) => {
  try {
    const eventID =
      purchaseEventId || getPurchaseEventId(orderId) || `order_${orderId}`;
    const storageKey = `fbq_purchase_${eventID}`;

    // Deduplication: Only fire once per order
    if (localStorage.getItem(storageKey)) {
      console.log("✓ Purchase already tracked for order:", orderId);
      return;
    }

    const value = orderValue ? parseFloat(orderValue) : 0;

    // Read utm_source from sessionStorage
    let sourceVal = "";
    try {
      const sourceStr = sessionStorage.getItem("utm_source");
      if (sourceStr) {
        sourceVal = JSON.parse(sourceStr).value;
      }
    } catch (e) {
      console.error("Error parsing utm_source:", e);
    }

    // ────── GA4 Tracking via dataLayer ──────
    try {
      if (window.dataLayer) {
        window.dataLayer.push({
          event: "purchase",
          ecommerce: {
            transaction_id: orderId,
            affiliation: "Naatu Sakkarai",
            value: value,
            currency: "INR",
            items: [
              {
                item_id: orderId,
                item_name: "Purchase",
                price: value,
                quantity: 1,
              },
            ],
          },
        });
        console.log("✓ GA4 Purchase Event Sent:", {
          transaction_id: orderId,
          value: value,
        });
      }
    } catch (ga4Err) {
      console.error("GA4 Tracking Error:", ga4Err);
    }

    // ────── Meta Pixel Tracking ──────
    try {
      if (window.fbq) {
        const metaPayload = {
          value: value,
          currency: "INR",
          content_ids: [orderId],
          contents: [
            {
              id: orderId,
              quantity: 1,
              price: value,
            },
          ],
          content_type: "product",
          num_items: 1,
        };

        const options = { eventID }; // For CAPI deduplication

        const customEventName = `purchase_${orderId}`;

        if (sourceVal && sourceVal.toLowerCase() === "metadh") {
          // Fire only for metaDH pixel
          window.fbq(
            "trackSingleCustom",
            "238260475866409",
            customEventName,
            metaPayload,
            options,
          );
          console.log(`✓ Meta Pixel DH Custom Purchase Event Sent (${customEventName})`);
        } else if (sourceVal && sourceVal.toLowerCase() === "apmeta") {
          // Fire only for APmeta pixel
          window.fbq(
            "trackSingleCustom",
            "831231161987100",
            customEventName,
            metaPayload,
            options,
          );
          console.log(`✓ Meta Pixel AP Custom Purchase Event Sent (${customEventName})`);
        } else {
          // Default: Fire to all pixels
          window.fbq("trackCustom", customEventName, metaPayload, options);
          console.log(`✓ Meta Pixel Custom Purchase Event Sent (Both: ${customEventName})`);
        }
      }
    } catch (metaErr) {
      console.error("Meta Pixel Tracking Error:", metaErr);
    }

    // Mark as tracked to prevent duplicate events
    localStorage.setItem(storageKey, "1");
    console.log("✓ Purchase tracking complete for order:", orderId);
  } catch (error) {
    console.error("Error in fireBrowserPurchase:", error);
  }
};
// ─────────────────────────────────────────────────────────────────────────────

const PaymentProvider = ({ children, ...props }) => {
  const [paymentCreateLoader, setPaymentCreateLoader] = useState(false);
  const [paymentSuccessLoader, setPaymentSuccessLoader] = useState(false);
  const [paymentFailedLoader, setPaymentFailedLoader] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  async function createPayment(callback) {
    console.log("createPayment: called");
    const purchaseEventId = createPurchaseEventId();
    setPurchaseEventId(purchaseEventId);
    const source = sessionStorage.getItem("utm_source");
    const medium = sessionStorage.getItem("utm_medium");
    const campaign = sessionStorage.getItem("utm_campaign");
    //get cookie from browser _fbc & _fbp
    // const fbc = document.cookie
    //   .split("; ")
    //   .find((row) => row.startsWith("_fbc="));
    // const fbp = document.cookie
    //   .split("; ")
    //   .find((row) => row.startsWith("_fbp="));
    setPaymentCreateLoader(true);
    const params = new URLSearchParams();
    if (source) params.append("source", JSON.parse(source).value);
    if (medium) params.append("medium", JSON.parse(medium).value);
    if (campaign) params.append("campaign", JSON.parse(campaign).value);
    params.append("event_id", purchaseEventId);
    const fbcCookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("_fbc="));
    const fbpCookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("_fbp="));

    const fbc = fbcCookie ? fbcCookie.split("=")[1] : "";
    const fbp = fbpCookie ? fbpCookie.split("=")[1] : "";
    if (fbc) params.append("fbc", fbc);
    if (fbp) params.append("fbp", fbp);
    const storedSession = localStorage.getItem("customer_session");
    let parsedSession = JSON.parse(storedSession);
    if (parsedSession?.value) {
      addSessionOverview(parsedSession.value, {
        source,
        medium,
        campaign,
        fbc,
        fbp,
      });
    }

    const url = `orders.php?action=createorder&${params.toString()}`;
    console.log("createPayment: calling API", url);
    await create(url, {})
      .then((res) => {
        if (res?.status === 200 && res?.success === true) {
          callback(res?.data);
          message.success(res?.message);
          setPaymentCreateLoader(false);
        } else {
          message.error(res?.message);
          setPaymentCreateLoader(false);
        }
      })
      .catch((err) => {
        message.error("Something went wrong");
        setPaymentCreateLoader(false);
        console.error("createPayment: API error", err);
      });
  }

  async function createPaymentOne(callback) {
    console.log("createPaymentOne: called");
    const purchaseEventId = createPurchaseEventId();
    setPurchaseEventId(purchaseEventId);
    const source = sessionStorage.getItem("utm_source");
    const medium = sessionStorage.getItem("utm_medium");
    const campaign = sessionStorage.getItem("utm_campaign");
    const fbcCookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("_fbc="));
    const fbpCookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("_fbp="));

    const fbc = fbcCookie ? fbcCookie.split("=")[1] : "";
    const fbp = fbpCookie ? fbpCookie.split("=")[1] : "";
    setPaymentCreateLoader(true);
    const params = new URLSearchParams();
    if (source) params.append("source", JSON.parse(source).value);
    if (medium) params.append("medium", JSON.parse(medium).value);
    if (campaign) params.append("campaign", JSON.parse(campaign).value);
    params.append("event_id", purchaseEventId);
    if (fbc) params.append("fbc", fbc);
    if (fbp) params.append("fbp", fbp);

    const storedSession = localStorage.getItem("customer_session");
    let parsedSession = JSON.parse(storedSession);
    if (parsedSession?.value) {
      addSessionOverview(parsedSession.value, {
        source,
        medium,
        campaign,
        fbc,
        fbp,
      });
    }

    const url = `orders_phonepay.php?action=createorder&${params.toString()}`;
    console.log("createPaymentOne: calling API", url);
    await create(url, {})
      .then((res) => {
        if (res?.status === 200 && res?.success === true) {
          callback(res?.data);
          message.success(res?.message);
          setPaymentCreateLoader(false);
        } else {
          message.error(res?.message);
          setPaymentCreateLoader(false);
        }
      })
      .catch((err) => {
        message.error("Something went wrong");
        setPaymentCreateLoader(false);
        console.error("createPaymentOne: API error", err);
      });
  }

  const addSessionOverview = async (sessionId, source) => {
    try {
      const res = await create("cart.php?action=updatecheckoutsession", {
        session_id: sessionId,
        referring_channels: JSON.stringify(source),
      });

      if (res?.status === 200 && res?.success === true) {
        console.log("Session updated successfully.");
      } else {
        console.error("Failed to update session.");
      }
    } catch (err) {
      console.error("Error in updating session:", err);
    }
  };

  async function paymentSuccess(
    razorpay_order_id,
    razorpay_payment_id,
    razorpay_signature,
    navigate,
    callbackSuccess,
  ) {
    setPaymentSuccessLoader(true);
    await create("orders.php?action=paymentsuccess", {
      razorpay_order_id: razorpay_order_id,
      razorpay_payment_id: razorpay_payment_id,
      razorpay_signature: razorpay_signature,
    })
      .then((res) => {
        if (
          res?.status === 200 &&
          res?.success === true &&
          res?.data?.redirect === "success"
        ) {
          navigate("/success", {
            state: {
              freecashMsg: res.data.freecash_msg,
            },
          });

          localStorage.setItem(
            "nattu_cart",
            JSON.stringify({ cart_items: [] }),
          );
          // Fire browser-side pixel Purchase (non-blocking, fire and forget)
          fireBrowserPurchase(
            res?.data?.order_id || razorpay_order_id,
            res?.data?.order_value,
            res?.data?.event_id,
          ).catch((err) => console.error("Purchase tracking error:", err));
          callbackSuccess();
          message.success(res?.message);
          setPaymentSuccessLoader(false);
        } else {
          if (res?.message === "Payment Already Done") {
            navigate("/success", {
              state: {
                freecashMsg: res.data.freecash_msg,
              },
            });
            localStorage.setItem(
              "nattu_cart",
              JSON.stringify({ cart_items: [] }),
            );
            // Fire browser-side pixel Purchase (non-blocking, already done case)
            fireBrowserPurchase(
              res?.data?.order_id || razorpay_order_id,
              res?.data?.order_value,
              res?.data?.event_id,
            ).catch((err) => console.error("Purchase tracking error:", err));
            callbackSuccess();
            message.success("Payment Successfully");
          }
          if (res?.message !== "Payment Already Done") {
            message.error(res?.message);
          }

          setPaymentSuccessLoader(false);
        }
      })
      .catch((err) => {
        message.error("Something went wrong");
        setPaymentSuccessLoader(false);
      });
  }

  async function paymentSuccessOne(order_id, callBackAfter) {
    setPaymentSuccessLoader(true);
    await create(`orders_phonepay.php?action=paymentsuccess`, {
      order_id: order_id,
    })
      .then((res) => {
        if (
          res?.status === 200 &&
          res?.success === true &&
          res?.data?.redirect === "success"
        ) {
          setPaymentStatus("success");
          localStorage.setItem(
            "nattu_cart",
            JSON.stringify({ cart_items: [] }),
          );
          // Fire browser-side pixel Purchase (non-blocking, PhonePe flow)
          fireBrowserPurchase(
            res?.data?.order_id || order_id,
            res?.data?.order_value,
            res?.data?.event_id,
          ).catch((err) => console.error("Purchase tracking error:", err));
          // callbackSuccess();
          callBackAfter && callBackAfter();
          message.success(res?.message);
          setPaymentSuccessLoader(false);
        } else {
          if (res?.message === "Payment Already Done") {
            setPaymentStatus("success");
            localStorage.setItem(
              "nattu_cart",
              JSON.stringify({ cart_items: [] }),
            );
            // Fire browser-side pixel Purchase (non-blocking, PhonePe already done case)
            fireBrowserPurchase(
              res?.data?.order_id || order_id,
              res?.data?.order_value,
              res?.data?.event_id,
            ).catch((err) => console.error("Purchase tracking error:", err));
            // callbackSuccess();
            callBackAfter && callBackAfter();
            message.success("Payment Successfully");
          }
          if (res?.message !== "Payment Already Done") {
            setPaymentStatus("failed");
            // message.error(res?.message);
          }

          setPaymentSuccessLoader(false);
        }
      })
      .catch((err) => {
        setPaymentStatus("failed");
        callBackAfter && callBackAfter();
        // message.error("Something went wrong");
        setPaymentSuccessLoader(false);
      });
  }

  async function paymentFailed(order_id, payment_id, navigate) {
    setPaymentFailedLoader(true);
    await create("orders.php?action=paymentfailed", {
      order_id: order_id,
      payment_id: payment_id,
    })
      .then((res) => {
        if (
          res?.status === 200 &&
          res?.success === true &&
          res?.data?.redirect === "error"
        ) {
          navigate("/failed");
          message.success(res?.message);
          setPaymentFailedLoader(false);
        } else {
          message.error(res?.message);
          setPaymentFailedLoader(false);
        }
      })
      .catch((err) => {
        message.error("Something went wrong");
        setPaymentFailedLoader(false);
      });
  }
  return (
    <Context.Provider
      value={{
        ...props,
        paymentCreateLoader,
        setPaymentCreateLoader,
        createPayment,
        createPaymentOne,
        paymentSuccess,
        paymentSuccessLoader,
        setPaymentSuccessLoader,
        paymentFailedLoader,
        setPaymentFailedLoader,
        paymentFailed,
        paymentSuccessOne,
        paymentStatus,
        setPaymentStatus,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default PaymentProvider;
